import React, { useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { Badge, Card, CardHeader, Col, Row, Table } from "reactstrap";
import {
  faFileExcel,
  faFileInvoice,
  faFileInvoiceDollar,
  faTruck,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import HubLinkCard from "../../common/HubLinkCard";
import ExpenseImportModal from "../ExpenseImportModal";
import Money from "./Money";
import { pendingAmountOf } from "./useFinanceHubData";

// El tipo 3 son los gastos individuales: tienen su propia pantalla porque se
// cargan a una propiedad y no se reparten por alícuota.
const INDIVIDUAL_EXPENSE_TYPE = 3;
const PENDING_DEBTS_PREVIEW = 5;

// Las pantallas de gastos leen el tipo del location.state, igual que cuando
// se llegaba desde las tarjetas de Contabilidad.
const expenseTypeCard = (type, base) => {
  const isIndividual = type.id === INDIVIDUAL_EXPENSE_TYPE;
  const path = `${base}/${isIndividual ? "individual-costs" : "common-costs"}`;
  const state = { type_id: type.id, type_name: type.name };

  return {
    key: `type-${type.id}`,
    to: { pathname: path, state },
    icon: isIndividual ? faUser : faFileInvoice,
    color: "warning",
    title: type.name,
    description: isIndividual
      ? "Gastos que se cargan a una propiedad concreta."
      : "Gastos que se reparten entre las propiedades según su alícuota.",
    action: { to: { pathname: `${path}/create`, state }, label: "Registrar gasto" },
  };
};

const PendingDebts = ({ debts, base }) => (
  <Card>
    <CardHeader className="d-flex justify-content-between align-items-center">
      <h5 className="mb-0">Cuentas por pagar pendientes</h5>
      <Link to={`${base}/topay`} className="fs--1 font-weight-semi-bold">
        Ver todas
      </Link>
    </CardHeader>
    <Table responsive className="fs--1 mb-0">
      <thead className="bg-200 text-900">
        <tr>
          <th>Concepto</th>
          <th>Tipo de gasto</th>
          <th className="text-right">Pendiente</th>
        </tr>
      </thead>
      <tbody>
        {debts.slice(0, PENDING_DEBTS_PREVIEW).map((debt) => (
          <tr key={debt.id}>
            <td>{debt.name || debt.finance?.name}</td>
            <td>{debt.finance?.type?.name || "—"}</td>
            <td className="text-right text-nowrap font-weight-semi-bold">
              <Money value={pendingAmountOf(debt)} />
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
    {debts.length > PENDING_DEBTS_PREVIEW && (
      <p className="fs--1 text-600 mb-0 px-3 py-2 border-top border-200">
        Mostrando {PENDING_DEBTS_PREVIEW} de {debts.length} cuentas pendientes.
      </p>
    )}
  </Card>
);

PendingDebts.propTypes = {
  debts: PropTypes.array.isRequired,
  base: PropTypes.string.isRequired,
};

const FinanceExpenses = ({ data, base, role, condominiumId }) => {
  const isManager = role === "manager";
  const pendingCount = data.pendingDebts.length;
  const [isImportOpen, setIsImportOpen] = useState(false);

  const cards = [
    ...(isManager ? data.expenseTypes.map((type) => expenseTypeCard(type, base)) : []),
    isManager && {
      key: "topay",
      to: `${base}/topay`,
      icon: faFileInvoiceDollar,
      color: "warning",
      title: "Cuentas por pagar",
      description: "Facturas de proveedores pendientes de pago.",
      meta:
        pendingCount > 0 ? (
          <Badge color="soft-warning" pill className="text-nowrap">
            {pendingCount} {pendingCount === 1 ? "pendiente" : "pendientes"}
          </Badge>
        ) : null,
      action: { to: `${base}/topay/create`, label: "Registrar cuenta" },
    },
    {
      key: "providers",
      to: `${base}/payments-provider`,
      icon: faTruck,
      color: "secondary",
      title: "Pagos a proveedores",
      description: "Lo que ya se les pagó a los proveedores del condominio.",
    },
    isManager && {
      key: "import",
      onClick: () => setIsImportOpen(true),
      icon: faFileExcel,
      color: "success",
      title: "Importar gastos",
      description:
        "Carga varios gastos de una vez desde un archivo Excel, CSV o texto: ordinarios, extraordinarios, del condominio y particulares.",
    },
  ].filter(Boolean);

  return (
    <>
      <p className="text-700 mb-3">
        Todo lo que sale: los gastos del período, lo que se debe a los proveedores y lo que
        ya se les pagó.
      </p>
      <Row>
        {cards.map(({ key, ...card }) => (
          <Col key={key} md={6} xl={4} className="mb-3">
            <HubLinkCard {...card} />
          </Col>
        ))}
      </Row>
      {isManager && pendingCount > 0 && <PendingDebts debts={data.pendingDebts} base={base} />}
      {isManager && (
        <ExpenseImportModal
          isOpen={isImportOpen}
          toggle={() => setIsImportOpen(false)}
          selectedCondominium={condominiumId}
          onSuccess={data.reload}
        />
      )}
    </>
  );
};

FinanceExpenses.propTypes = {
  data: PropTypes.object.isRequired,
  base: PropTypes.string.isRequired,
  role: PropTypes.string,
  condominiumId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default FinanceExpenses;
